/**
 * The short, readable name for a stored file's media type — `application/pdf` → `PDF`.
 *
 * Every surface that states what KIND of file something is goes through here, for the reason
 * every size goes through `formatBytes`: a label written at its call site is one that will be
 * written differently at the next call site.
 *
 * **It is fed the VERIFIED content type, never a filename.** `StoredFileService` refuses any
 * upload whose leading bytes contradict its declared type, so `fileContentType` is evidence —
 * where the extension on `fileOriginalName` is only the uploader's word for what the bytes are.
 * Deriving a type label from a filename would state a fact this system deliberately never
 * trusts.
 */

/**
 * The four types the product actually admits — `FileCategoryRegistry` on the server.
 *
 * A map rather than a derivation for these, because the subtype alone is wrong for two of
 * them the moment a fifth arrives: an Office document's subtype is a forty-character
 * `vnd.openxmlformats-…` string that no reader wants in a header line.
 */
const NAMES: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'text/csv': 'CSV',
};

export function formatFileType(contentType: string | null | undefined): string | null {
  if (!contentType) {
    return null;
  }

  // Parameters are stripped first — a stored type can arrive as `text/csv; charset=utf-8`,
  // and the part before the semicolon is the whole of what identifies it.
  const type = contentType.split(';')[0].trim().toLowerCase();

  if (type === '') {
    return null;
  }

  if (NAMES[type]) {
    return NAMES[type];
  }

  /*
    The subtype, uppercased — an honest guess for a type the map does not carry, and better
    than printing the whole media type into a line of prose. `x-` is dropped because it is a
    registry convention rather than part of the name a reader would use.

    A type with no slash is returned as it stands rather than discarded: it is malformed, and
    showing what was stored says more than showing nothing.
  */
  const subtype = type.includes('/') ? type.slice(type.indexOf('/') + 1) : type;

  return subtype.replace(/^x-/, '').toUpperCase();
}
